import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';
import { extractErrorMessage } from '../lib/apiError';
import { useWebSocket } from '../context/WebSocketContext';
import {
  Building2,
  Bell,
  FileText,
  Wrench,
  Calendar,
  CreditCard,
  Users,
  Home,
  Settings,
  LogOut,
  Plus,
  Upload,
  Send,
  Search,
  Menu,
  X,
  Mail,
  Phone,
  MapPin,
  Clock,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Trash2,
  Download,
  UserPlus,
  Loader2,
  Shield,
  UserCog,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const sidebarItems = [
  { icon: Building2, label: 'Dashboard', path: '/admin' },
  { icon: Bell, label: 'Notices', path: '/admin/notices' },
  { icon: FileText, label: 'Documents', path: '/admin/documents' },
  { icon: Wrench, label: 'Maintenance', path: '/admin/maintenance' },
  { icon: Calendar, label: 'Events', path: '/admin/events' },
  { icon: CreditCard, label: 'Payments', path: '/admin/payments' },
  { icon: Users, label: 'Residents', path: '/admin/residents' },
  { icon: Settings, label: 'Settings', path: '/admin/settings' },
];

const statusColors = {
  pending: 'bg-amber-100 text-amber-700',
  accepted: 'bg-emerald-100 text-emerald-700',
  expired: 'bg-slate-100 text-slate-600',
  active: 'bg-emerald-100 text-emerald-700',
};

export default function AdminResidents() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [residents, setResidents] = useState([]);
  const [invites, setInvites] = useState([]);
  const [pendingMembers, setPendingMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('pending');
  const [actioningMemberId, setActioningMemberId] = useState(null);
  const [rejectingMember, setRejectingMember] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  
  // Dialogs
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [isBulkOpen, setIsBulkOpen] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [residentToDelete, setResidentToDelete] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleting, setDeleting] = useState(false);
  
  // Form data
  const [inviteData, setInviteData] = useState({
    name: '',
    email: '',
    unit_number: '',
    role: 'resident', // 'resident' or 'trustee'
  });
  
  const [bulkData, setBulkData] = useState([]);
  const [bulkPreview, setBulkPreview] = useState([]);

  const hoaId = user?.hoa_id || 'demo_hoa';
  const token = localStorage.getItem('session_token');
  const { notifications } = useWebSocket();

  useEffect(() => {
    fetchData();
  }, [hoaId]);

  // Real-time refresh: when a member_request / member_approved / member_rejected event
  // arrives via WebSocket, silently refetch the pending list.
  useEffect(() => {
    if (!notifications || notifications.length === 0) return;
    const latest = notifications[0];
    if (latest && ['member_request', 'member_approved', 'member_rejected'].includes(latest.type)) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifications]);

  const deleteResident = async () => {
    if (!residentToDelete) return;
    setDeleting(true);
    try {
      const res = await fetch(`${API_URL}/api/user-management/users/${residentToDelete.user_id}/archive`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: deleteReason || 'Removed by community admin' }),
      });
      if (res.ok) {
        toast.success(`${residentToDelete.name} deleted (soft-archived)`);
        setResidents(rs => rs.map(r => r.user_id === residentToDelete.user_id ? { ...r, status: 'archived' } : r));
        setResidentToDelete(null);
        setDeleteReason('');
      } else {
        const e = await res.json().catch(() => ({}));
        toast.error(extractErrorMessage(e, 'Could not delete resident'));
      }
    } catch {
      toast.error('Network error');
    } finally { setDeleting(false); }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch residents (users in this HOA)
      const usersRes = await fetch(`${API_URL}/api/hoa/${hoaId}/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (usersRes.ok) {
        const usersData = await usersRes.json();
        setResidents(usersData);
      }
      
      // Fetch invites
      const invitesRes = await fetch(`${API_URL}/api/onboarding/invites/${hoaId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (invitesRes.ok) {
        const invitesData = await invitesRes.json();
        setInvites(invitesData);
      }

      // Fetch pending building membership requests (access_status='invited' on user_buildings)
      const pendingRes = await fetch(`${API_URL}/api/buildings/${hoaId}/members?status_filter=invited`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (pendingRes.ok) {
        const pendingData = await pendingRes.json();
        setPendingMembers(pendingData.members || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const approveMember = async (member) => {
    setActioningMemberId(member.user_id);
    try {
      const res = await fetch(`${API_URL}/api/buildings/${hoaId}/members/${member.user_id}/approve`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: null }),
      });
      if (res.ok) {
        toast.success(`${member.name || member.email} approved as resident`);
        // Optimistic update: remove from pending list immediately, refresh real lists in bg
        setPendingMembers((rows) => rows.filter((r) => r.user_id !== member.user_id));
        fetchData();
      } else {
        const e = await res.json().catch(() => ({}));
        toast.error(extractErrorMessage(e, 'Could not approve member'));
      }
    } catch {
      toast.error('Network error');
    } finally {
      setActioningMemberId(null);
    }
  };

  const rejectMember = async () => {
    if (!rejectingMember) return;
    setActioningMemberId(rejectingMember.user_id);
    try {
      const res = await fetch(`${API_URL}/api/buildings/${hoaId}/members/${rejectingMember.user_id}/reject`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: rejectReason || null }),
      });
      if (res.ok) {
        toast.success(`${rejectingMember.name || rejectingMember.email} rejected`);
        setPendingMembers((rows) => rows.filter((r) => r.user_id !== rejectingMember.user_id));
        setRejectingMember(null);
        setRejectReason('');
        fetchData();
      } else {
        const e = await res.json().catch(() => ({}));
        toast.error(extractErrorMessage(e, 'Could not reject member'));
      }
    } catch {
      toast.error('Network error');
    } finally {
      setActioningMemberId(null);
    }
  };

  const handleInviteSingle = async () => {
    if (!inviteData.name || !inviteData.email || !inviteData.unit_number) {
      toast.error('Please fill in all fields');
      return;
    }

    setInviteLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/onboarding/invite/single`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          hoa_id: hoaId,
          ...inviteData,
        }),
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(extractErrorMessage(data, 'Failed to send invite'));
      }

      toast.success(`Invitation sent to ${inviteData.email}`);
      setIsInviteOpen(false);
      setInviteData({ name: '', email: '', unit_number: '', role: 'resident' });
      fetchData();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to send invite'));
    } finally {
      setInviteLoading(false);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result;
      const lines = text.split('\n').filter(line => line.trim());
      
      // Skip header row
      const dataRows = lines.slice(1);
      
      const parsed = dataRows.map(line => {
        const [name, email, unit_number] = line.split(',').map(s => s.trim());
        return { name, email, unit_number };
      }).filter(row => row.email && row.name);
      
      setBulkPreview(parsed);
      setIsBulkOpen(true);
    };
    reader.readAsText(file);
    
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleBulkInvite = async () => {
    if (bulkPreview.length === 0) {
      toast.error('No valid entries to invite');
      return;
    }

    setInviteLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/onboarding/invite/bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          hoa_id: hoaId,
          residents: bulkPreview,
        }),
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to send invites');
      }

      toast.success(`${data.success_count} invitations sent successfully`);
      if (data.skipped_count > 0) {
        toast.info(`${data.skipped_count} entries skipped (already registered)`);
      }
      
      setIsBulkOpen(false);
      setBulkPreview([]);
      fetchData();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Bulk invite failed'));
    } finally {
      setInviteLoading(false);
    }
  };

  const handleResendInvite = async (inviteId) => {
    try {
      const response = await fetch(`${API_URL}/api/onboarding/invites/${inviteId}/resend`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to resend invite');
      }

      toast.success('Invitation resent');
      fetchData();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to resend invite'));
    }
  };

  const handleCancelInvite = async (inviteId) => {
    if (!confirm('Cancel this invitation?')) return;

    try {
      const response = await fetch(`${API_URL}/api/onboarding/invites/${inviteId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to cancel invite');
      }

      toast.success('Invitation cancelled');
      fetchData();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to cancel invite'));
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const downloadTemplate = () => {
    const csv = 'name,email,unit_number\nJohn Smith,john@example.com,101\nJane Doe,jane@example.com,102';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'residents_template.csv';
    a.click();
  };

  const filteredResidents = residents.filter(r =>
    r.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    r.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredInvites = invites.filter(i =>
    i.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    i.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-slate-900 transform transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-4 border-b border-slate-800">
            <Link to="/admin" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-sky-500 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-bold text-white">BodyCorp365</p>
                <p className="text-xs text-slate-400">Admin Portal</p>
              </div>
            </Link>
            <Button variant="ghost" size="icon" className="lg:hidden text-slate-400" onClick={() => setSidebarOpen(false)}>
              <X className="w-5 h-5" />
            </Button>
          </div>

          <nav className="flex-1 overflow-y-auto py-4 px-3">
            <ul className="space-y-1">
              {sidebarItems.map((item) => (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                      item.path === '/admin/residents'
                        ? 'bg-sky-500 text-white'
                        : 'text-slate-300 hover:text-white hover:bg-slate-800'
                    }`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div className="p-4 border-t border-slate-800">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-sky-600 flex items-center justify-center text-white font-medium">
                {user?.name?.charAt(0) || 'A'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name || 'Admin'}</p>
                <p className="text-xs text-slate-400 truncate">{user?.role}</p>
              </div>
              <Button variant="ghost" size="icon" className="text-slate-400 hover:text-white" onClick={handleLogout}>
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="fixed top-0 left-0 right-0 z-40 lg:hidden bg-white border-b border-slate-200">
        <div className="flex items-center justify-between px-4 py-3">
          <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(true)}>
            <Menu className="w-6 h-6" />
          </Button>
          <span className="font-bold text-slate-900">Residents</span>
          <div className="w-10" />
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 lg:ml-64 pt-16 lg:pt-0">
        <div className="p-4 lg:p-8 max-w-6xl mx-auto">
          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">Residents & Invites</h1>
              <p className="text-slate-500 mt-1">Manage your community members</p>
            </div>
            <div className="flex gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                className="hidden"
              />
              <Button 
                variant="outline" 
                onClick={() => fileInputRef.current?.click()}
                data-testid="bulk-upload-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload CSV
              </Button>
              <Button 
                className="bg-sky-500 hover:bg-sky-600"
                onClick={() => setIsInviteOpen(true)}
                data-testid="invite-resident-btn"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Invite Resident
              </Button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-slate-500">Total Residents</p>
                <p className="text-2xl font-bold text-slate-900">{residents.length}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-amber-600">Pending Invites</p>
                <p className="text-2xl font-bold text-amber-600">{invites.filter(i => i.status === 'pending').length}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-emerald-600">Accepted</p>
                <p className="text-2xl font-bold text-emerald-600">{invites.filter(i => i.status === 'accepted').length}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-slate-500">Occupancy</p>
                <p className="text-2xl font-bold text-slate-900">
                  {residents.length > 0 ? `${Math.round((residents.length / 24) * 100)}%` : '0%'}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Search */}
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-residents"
            />
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="pending" data-testid="tab-pending-approvals">
                <Clock className="w-4 h-4 mr-2" />
                Pending Approvals ({pendingMembers.length})
              </TabsTrigger>
              <TabsTrigger value="residents" data-testid="tab-residents">
                <Users className="w-4 h-4 mr-2" />
                Residents ({residents.length})
              </TabsTrigger>
              <TabsTrigger value="invites" data-testid="tab-invites">
                <Mail className="w-4 h-4 mr-2" />
                Invites ({invites.length})
              </TabsTrigger>
            </TabsList>

            {/* Pending Approvals Tab */}
            <TabsContent value="pending">
              {loading ? (
                <Card>
                  <CardContent className="p-8 text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-slate-400 mx-auto" />
                  </CardContent>
                </Card>
              ) : pendingMembers.length === 0 ? (
                <Card data-testid="no-pending-card">
                  <CardContent className="p-12 text-center">
                    <CheckCircle2 className="w-12 h-12 text-emerald-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">No pending requests</h3>
                    <p className="text-slate-500">When new users request access to your building, they&apos;ll appear here for approval.</p>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Requested Role</TableHead>
                        <TableHead>Requested</TableHead>
                        <TableHead>Joined via</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pendingMembers.map((m) => (
                        <TableRow key={m.user_id} data-testid={`pending-row-${m.user_id}`}>
                          <TableCell className="font-medium">{m.name || '—'}</TableCell>
                          <TableCell className="text-slate-600">{m.email || '—'}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="capitalize">{m.role || 'resident'}</Badge>
                          </TableCell>
                          <TableCell className="text-slate-500 text-sm">
                            {m.joined_at ? new Date(m.joined_at).toLocaleDateString() : '—'}
                          </TableCell>
                          <TableCell className="text-slate-500 text-sm capitalize">
                            {(m.joined_via || 'request').replace(/_/g, ' ')}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex gap-2 justify-end">
                              <Button
                                size="sm"
                                className="bg-emerald-500 hover:bg-emerald-600 text-white"
                                onClick={() => approveMember(m)}
                                disabled={actioningMemberId === m.user_id}
                                data-testid={`approve-btn-${m.user_id}`}
                              >
                                {actioningMemberId === m.user_id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>
                                    <CheckCircle2 className="w-4 h-4 mr-1" />
                                    Approve
                                  </>
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-red-200 text-red-600 hover:bg-red-50"
                                onClick={() => setRejectingMember(m)}
                                disabled={actioningMemberId === m.user_id}
                                data-testid={`reject-btn-${m.user_id}`}
                              >
                                <XCircle className="w-4 h-4 mr-1" />
                                Reject
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Card>
              )}
            </TabsContent>

            {/* Residents Tab */}
            <TabsContent value="residents">
              {loading ? (
                <Card>
                  <CardContent className="p-8 text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-slate-400 mx-auto" />
                  </CardContent>
                </Card>
              ) : filteredResidents.length === 0 ? (
                <Card>
                  <CardContent className="p-12 text-center">
                    <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">No residents yet</h3>
                    <p className="text-slate-500 mb-4">Start by inviting residents to your community</p>
                    <Button onClick={() => setIsInviteOpen(true)} className="bg-sky-500 hover:bg-sky-600">
                      <UserPlus className="w-4 h-4 mr-2" />
                      Invite First Resident
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Unit</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredResidents.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6}><div className="text-center text-slate-400 py-6">No residents yet</div></TableCell>
                        </TableRow>
                      ) : null}
                      {filteredResidents.map((resident) => (
                        <TableRow key={resident.user_id} data-testid={`resident-${resident.user_id}`}>
                          <TableCell className="font-medium">{resident.name}</TableCell>
                          <TableCell>{resident.email}</TableCell>
                          <TableCell>{resident.unit_id?.split('_').pop() || '-'}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{resident.role}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={statusColors[resident.status] || statusColors.active}>
                              {resident.status || 'active'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            {resident.status !== 'archived' ? (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setResidentToDelete(resident)}
                                className="border-red-300 text-red-600 hover:bg-red-50"
                                data-testid={`delete-resident-${resident.user_id}`}
                              >
                                <Trash2 className="w-4 h-4 mr-1" /> Delete
                              </Button>
                            ) : (
                              <span className="text-xs text-slate-400" data-testid={`archived-label-${resident.user_id}`}>Deleted</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Card>
              )}
            </TabsContent>

            {/* Invites Tab */}
            <TabsContent value="invites">
              {loading ? (
                <Card>
                  <CardContent className="p-8 text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-slate-400 mx-auto" />
                  </CardContent>
                </Card>
              ) : filteredInvites.length === 0 ? (
                <Card>
                  <CardContent className="p-12 text-center">
                    <Mail className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">No invitations sent</h3>
                    <p className="text-slate-500 mb-4">Send invitations to add residents to your community</p>
                    <div className="flex gap-2 justify-center">
                      <Button variant="outline" onClick={downloadTemplate}>
                        <Download className="w-4 h-4 mr-2" />
                        Download CSV Template
                      </Button>
                      <Button onClick={() => setIsInviteOpen(true)} className="bg-sky-500 hover:bg-sky-600">
                        <UserPlus className="w-4 h-4 mr-2" />
                        Send Invite
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Unit</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Sent</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredInvites.map((invite) => (
                        <TableRow key={invite.invite_id} data-testid={`invite-${invite.invite_id}`}>
                          <TableCell className="font-medium">{invite.name}</TableCell>
                          <TableCell>{invite.email}</TableCell>
                          <TableCell>{invite.unit_number}</TableCell>
                          <TableCell>
                            <Badge className={statusColors[invite.status]}>
                              {invite.status === 'pending' && <Clock className="w-3 h-3 mr-1" />}
                              {invite.status === 'accepted' && <CheckCircle2 className="w-3 h-3 mr-1" />}
                              {invite.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-slate-500 text-sm">
                            {new Date(invite.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell className="text-right">
                            {invite.status === 'pending' && (
                              <div className="flex gap-1 justify-end">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleResendInvite(invite.invite_id)}
                                  data-testid={`resend-${invite.invite_id}`}
                                >
                                  <RefreshCw className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleCancelInvite(invite.invite_id)}
                                  data-testid={`cancel-${invite.invite_id}`}
                                >
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </main>

      {/* Invite Single Dialog */}
      <Dialog open={isInviteOpen} onOpenChange={setIsInviteOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Invite New Member</DialogTitle>
            <DialogDescription>
              Send an invitation to join your community
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            {/* Role Selection - Clear Visual Buttons */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Select Member Type *</Label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setInviteData({ ...inviteData, role: 'resident' })}
                  className={`relative p-4 rounded-xl border-2 transition-all text-left ${
                    inviteData.role === 'resident'
                      ? 'border-sky-500 bg-sky-50 ring-2 ring-sky-200'
                      : 'border-slate-200 hover:border-slate-300 bg-white'
                  }`}
                  data-testid="role-resident"
                >
                  {inviteData.role === 'resident' && (
                    <div className="absolute top-2 right-2">
                      <CheckCircle2 className="w-5 h-5 text-sky-500" />
                    </div>
                  )}
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-2 ${
                    inviteData.role === 'resident' ? 'bg-sky-500' : 'bg-slate-100'
                  }`}>
                    <Users className={`w-5 h-5 ${inviteData.role === 'resident' ? 'text-white' : 'text-slate-500'}`} />
                  </div>
                  <p className={`font-semibold ${inviteData.role === 'resident' ? 'text-sky-700' : 'text-slate-900'}`}>
                    Resident
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Standard access to community features
                  </p>
                </button>
                
                <button
                  type="button"
                  onClick={() => setInviteData({ ...inviteData, role: 'trustee' })}
                  className={`relative p-4 rounded-xl border-2 transition-all text-left ${
                    inviteData.role === 'trustee'
                      ? 'border-amber-500 bg-amber-50 ring-2 ring-amber-200'
                      : 'border-slate-200 hover:border-slate-300 bg-white'
                  }`}
                  data-testid="role-trustee"
                >
                  {inviteData.role === 'trustee' && (
                    <div className="absolute top-2 right-2">
                      <CheckCircle2 className="w-5 h-5 text-amber-500" />
                    </div>
                  )}
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-2 ${
                    inviteData.role === 'trustee' ? 'bg-amber-500' : 'bg-slate-100'
                  }`}>
                    <Shield className={`w-5 h-5 ${inviteData.role === 'trustee' ? 'text-white' : 'text-slate-500'}`} />
                  </div>
                  <p className={`font-semibold ${inviteData.role === 'trustee' ? 'text-amber-700' : 'text-slate-900'}`}>
                    Trustee / Admin
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Full access + Trustee Hub
                  </p>
                </button>
              </div>
              {inviteData.role === 'trustee' && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <Shield className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-amber-800">
                    <strong>Trustee Hub Access:</strong> This member will have access to voting, financial reports, 
                    contractor approvals, and admin dashboard features.
                  </p>
                </div>
              )}
            </div>

            <div className="border-t pt-4 space-y-4">
              <div className="space-y-2">
                <Label>Full Name *</Label>
                <Input
                  value={inviteData.name}
                  onChange={(e) => setInviteData({ ...inviteData, name: e.target.value })}
                  placeholder="John Smith"
                  data-testid="invite-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Email Address *</Label>
                <Input
                  type="email"
                  value={inviteData.email}
                  onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                  placeholder="john@example.com"
                  data-testid="invite-email"
                />
              </div>
              <div className="space-y-2">
                <Label>Unit Number *</Label>
                <Input
                  value={inviteData.unit_number}
                  onChange={(e) => setInviteData({ ...inviteData, unit_number: e.target.value })}
                  placeholder="101"
                  data-testid="invite-unit"
                />
              </div>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setIsInviteOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleInviteSingle} 
              disabled={inviteLoading}
              className={inviteData.role === 'trustee' ? 'bg-amber-500 hover:bg-amber-600' : 'bg-sky-500 hover:bg-sky-600'}
              data-testid="send-invite-btn"
            >
              {inviteLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Invite {inviteData.role === 'trustee' ? 'Trustee' : 'Resident'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Upload Preview Dialog */}
      <Dialog open={isBulkOpen} onOpenChange={setIsBulkOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Bulk Invite Preview</DialogTitle>
            <DialogDescription>
              Review the residents to be invited ({bulkPreview.length} found)
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-64 overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Unit</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bulkPreview.map((row, i) => (
                  <TableRow key={i}>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{row.email}</TableCell>
                    <TableCell>{row.unit_number}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsBulkOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleBulkInvite} 
              disabled={inviteLoading}
              className="bg-sky-500 hover:bg-sky-600"
              data-testid="confirm-bulk-invite"
            >
              {inviteLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Send {bulkPreview.length} Invitations
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Resident Confirm Dialog */}
      <Dialog open={!!residentToDelete} onOpenChange={(o) => { if (!o) { setResidentToDelete(null); setDeleteReason(''); } }}>
        <DialogContent className="sm:max-w-md" data-testid="delete-resident-dialog">
          <DialogHeader>
            <DialogTitle>Delete Resident</DialogTitle>
            <DialogDescription>
              {residentToDelete && (
                <>This will soft-delete <strong>{residentToDelete.name}</strong> ({residentToDelete.email}). Their record is preserved in your audit trail and can be restored by a Platform Owner if needed.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label>Reason (optional)</Label>
            <Textarea value={deleteReason} onChange={(e) => setDeleteReason(e.target.value)} rows={3} placeholder="e.g., No longer a resident, moved out" data-testid="delete-reason-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setResidentToDelete(null); setDeleteReason(''); }} data-testid="cancel-delete-btn">Cancel</Button>
            <Button onClick={deleteResident} disabled={deleting} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-btn">
              {deleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Delete Resident
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Pending Member Dialog */}
      <Dialog
        open={!!rejectingMember}
        onOpenChange={(o) => { if (!o) { setRejectingMember(null); setRejectReason(''); } }}
      >
        <DialogContent className="sm:max-w-md" data-testid="reject-member-dialog">
          <DialogHeader>
            <DialogTitle>Reject Access Request</DialogTitle>
            <DialogDescription>
              {rejectingMember && (
                <>This will reject the building access request from <strong>{rejectingMember.name || rejectingMember.email}</strong>. They will not be able to view or interact with this building&apos;s data.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label>Reason (optional, shared with the requester)</Label>
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              placeholder="e.g., Not a current owner / tenant"
              data-testid="reject-reason-input"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setRejectingMember(null); setRejectReason(''); }}
              data-testid="cancel-reject-btn"
            >Cancel</Button>
            <Button
              onClick={rejectMember}
              disabled={actioningMemberId === rejectingMember?.user_id}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-reject-btn"
            >
              {actioningMemberId === rejectingMember?.user_id && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Reject Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
}
